import { createContext, use, type ReactNode } from 'react'
import {
  SkeletonLoader,
  type SkeletonLoaderProps,
} from '@/features/shared/components/SkeletonLoader/SkeletonLoader'
import { type DistributiveOmit } from '@/features/shared/utils/types'

type ChildrenProps = { children: ReactNode }

type ProviderProps = Pick<SkeletonLoaderProps, 'isLoading'> & ChildrenProps

type ConsumerProps = DistributiveOmit<SkeletonLoaderProps, 'isLoading'> & ChildrenProps

const LoadingContext = createContext(false)

/**
 Context provider to allow child components access to parent loading state
 */
export const SkeletonProvider = ({ isLoading = false, children }: ProviderProps) => (
  <LoadingContext value={isLoading}>{children}</LoadingContext>
)

/**
 Context consumer requires context provider as parent to access isLoading prop
 This will have the same props structure as the component it wraps: SkeletonLoader
 */
export const SkeletonConsumer = ({ children, ...restProps }: ConsumerProps) => {
  const isLoading = use(LoadingContext)

  return (
    <SkeletonLoader {...restProps} isLoading={isLoading}>
      {children}
    </SkeletonLoader>
  )
}

export const useSkeletonLoaderContext = () => use(LoadingContext)
